import React, { useCallback, useEffect, useState, useMemo, useRef, useContext, ReactNode } from "react";
import { DragDropProvider, DragOverlay, useDroppable, PointerSensor } from "@dnd-kit/react";
import { DeliveryComponent, CurrentTimeContext } from "./delivery";

import { HeatMap } from "./heatmap";
import * as Other from "./other";
import { Dashboard, Driver, Delivery, DeliveryGroup, ApiClientInterface } from "./model";
import { classNames } from "src/common_util";
import { DashboardConfig, DashboardContext, useDashboardConfig } from "./dispatch_api_client";
import { AdminPreferences, AdminPreferencesTracker } from "controllers/admin/admin_preferences_tracker";

interface DispatchDashboardProps {
  apiClient: ApiClientInterface;
  currentAdminId: number;
}

interface TabNotification {
  order: number;
  driver: boolean;
}

interface DragData {
  type: string;
  id: string;
  unsetErrorTimer: () => void;
  handleDropping: () => void;
  handleDragFailure?: () => void;
}

const defaultTabNotification = {
  order: 0,
  driver: false
}

const DispatchDashboard = ({ apiClient, currentAdminId } : DispatchDashboardProps) => {
  const [ dashboard, setDashboard ] = useState(Dashboard.emptyDashboard());
  const [ connected, setConnected ] = useState(false);
  const [ now, setNow ] = useState(new Date());
  const [ busySection, setBusySection ] = useState(false);
  const [ draggedNode, setDraggedNode ] = useState<{ type: string; id: number | string } | null>(null);

  const originalDocumentTitle = useRef(document.title);
  const [ previousOrderNotificationAt, setPreviousOrderNotificationAt ] = useState(0);
  const [ previousDriverNotificationAt, setPreviousDriverNotificationAt ] = useState(0);
  const [ tabNotification, setTabNotification ] = useState<TabNotification>(defaultTabNotification);

  useEffect(() => {
    const deliverySorter = (a: Delivery, b: Delivery) : number => {
      // Sort order is pickupTime, pickupZoneId, storeName, suburbName, id
      if (a.pickupTimeIso8601 < b.pickupTimeIso8601)
        return -1;
      else if (a.pickupTimeIso8601 > b.pickupTimeIso8601)
        return 1;
      else if (a.storeName < b.storeName)
        return -1;
      else if (a.storeName > b.storeName)
        return 1;
      else if (a.suburb < b.suburb)
        return -1;
      else if (a.suburb > b.suburb)
        return 1;
      else if (a.id < b.id)
        return -1;
      else if (a.id > b.id)
        return 1;
      else
        return 0;
    }

    const handleDashboardUpdated = (newDashboard: Dashboard) => {
      newDashboard.deliveries.sort(deliverySorter);

      setDashboard(newDashboard);
    }

    const handleDashboardConnected = (connected: boolean) => {
      setConnected(connected);
    }

    const resetDocumentTitle = () => {
      setTabNotification(defaultTabNotification);
    }

    const intervalTimer = window.setInterval(() => setNow(new Date()), 5000);

    console.log("Starting API client on component connect");
    apiClient.dashboardUpdated.addListener(handleDashboardUpdated);
    apiClient.dashboardConnected.addListener(handleDashboardConnected);
    apiClient.start();

    window.addEventListener("focus", resetDocumentTitle);

    return () => {
      clearInterval(intervalTimer);

      window.removeEventListener("focus", resetDocumentTitle);

      console.log("Stopping API client on component disconnect");
      apiClient.stop();
      apiClient.dashboardConnected.removeListener(handleDashboardConnected);
      apiClient.dashboardUpdated.removeListener(handleDashboardUpdated);
    };
  }, [ apiClient ]);

  const descriptiveDriverList = (title : string, drivers: Driver[], showBusySectionToggle = false) : React.JSX.Element => {
    if (drivers.length == 0) {
      return <></>;
    }

    const handleBusySectionToggle = () => {
      setBusySection(!busySection);
    }

    let busySectionToggle = <></>;
    // if both busy sections are visible & it's the first busy section
    if (showBusySectionToggle && ((busySection && title == "Busy (picking up)") || (!busySection && title == "Busy (dropping off)"))) {
      busySectionToggle = <button className="button is-small is-text mr-2 has-tooltip-top has-tooltip-arrow" data-tooltip="Toggle busy sections" onClick={handleBusySectionToggle}>
        <span className="icon is-small">
          <i className="fa-solid fa-arrow-up-arrow-down"></i>
        </span>
      </button>;
    }

    const dashSeparator = title.startsWith("Busy")
      ? <span className="dash-separator"></span>
      : <></>;

    return <>
      <div className="is-flex mb-4 is-align-items-center">
        {busySectionToggle}
        <span className="is-size-5 is-flex-shrink-0">{title}</span>
        <span className="tag is-rounded is-white is-light ml-2">{drivers.length}</span>
        {dashSeparator}
      </div>

      <Other.DriverList
        dashboard={dashboard}
        drivers={drivers}
      />
    </>;
  }

  const hasCurfewHighlight = useMemo(() => {
    const hour = now.getHours();
    if (hour > 20 || hour < 5 || (hour == 20 && now.getMinutes() >= 45)) {
      return true;
    }
    return false;
  }, [now]);


  const handleDragStart = (event: { operation: { source: { data?: unknown } | null } }) => {
    const dragTarget = event.operation.source?.data as DragData | undefined;

    if (!dragTarget) return;

    dragTarget.unsetErrorTimer();
    setDraggedNode({ type: dragTarget.type, id: dragTarget.id });
  }

  const handleDragEnd = useCallback(async (event: { operation: { source: { data?: unknown } | null; target: { data?: unknown } | null }; canceled?: boolean }) => {
    setDraggedNode(null);

    // If drag was canceled, don't process the drop
    if (event.canceled) return;

    if (event.operation.target === null || !document.hasFocus()) return;

    const dragTarget = event.operation.source?.data as DragData | undefined;
    const dropTarget = event.operation.target?.data as DragData | undefined;

    if (!dragTarget || !dropTarget) return;

    if (dragTarget.type == "delivery") {
      if (dropTarget.type == "group") {
        dragTarget.handleDropping();
        await apiClient.allocateDeliveryToGroup(dragTarget.id, Number(dropTarget.id));
      }
      else if (dropTarget.type == "driver") {
        dragTarget.handleDropping();
        await dashboard.handleAllocateDeliveryToDriver(dragTarget.id, Number(dropTarget.id), apiClient)
          .then(result => {
            if (result === false && dragTarget.handleDragFailure) {
              dragTarget.handleDragFailure();
            }
          });
      }
    }

    else if (dragTarget.type == "group") {
      if (dropTarget.type == "group") {
        // Because the group's own container is a valid droppable, & merging a group to itself is a valid action
        // specifically check & ignore that case (there is also some CSS to prevent high
        // lighting the container in this case)
        if (dragTarget.id == dropTarget.id) return;

        dragTarget.handleDropping();
        await apiClient.mergeGroups(Number(dragTarget.id), Number(dropTarget.id));
      }
      else if (dropTarget.type == "driver") {
        dragTarget.handleDropping();
        await dashboard.handleAllocateGroupToDriver(Number(dragTarget.id), Number(dropTarget.id), apiClient)
          .then(result => {
            if (result === false && dragTarget.handleDragFailure) {
              dragTarget.handleDragFailure();
            }
          });
      }
    }
  }, [ dashboard, apiClient ]);

  const draggedNodeClone = useMemo(() => {
    if (draggedNode?.type == "delivery") {
      const delivery = dashboard.deliveries.find(d => d.id == draggedNode.id);

      if (delivery) {
        return <div className="card is-card-dashboard">
          <div className="card-content p-0">
            <DeliveryComponent
              delivery={delivery}
              defaultExpand={true}
              deliveringNow={false}
              dashboard={dashboard}
            />
          </div>
        </div>;
      }
    }

    else if (draggedNode?.type == "group") {
      const group = dashboard.groups.find(g => g.id == draggedNode.id);
      const deliveries = dashboard.deliveriesForGroup(draggedNode.id as number);

      if (group && deliveries.length > 0) {
        return <Other.DeliveryGroupComponent
          group={group}
          deliveries={deliveries}
          dashboard={dashboard}
        />;
      }
    }

    return null;
  }, [draggedNode, dashboard]);


  const allActiveGroupIds = dashboard.deliveries.map(o => o.groupId);
  const activeDeliveryGroups = dashboard.groups.filter(og => allActiveGroupIds.includes(og.id));
  const anyGroupedDeliveries = activeDeliveryGroups.length > 0;
  const deliveryGroupList = anyGroupedDeliveries ? <DeliveryGroupList
    dashboard={dashboard}
    deliveriesByGroup={dashboard.deliveriesByGroup()}
    apiClient={apiClient}
  /> : <></>;

  const busyPickingUp = dashboard.driversBusyPickingUp();
  const busyDroppingOff = dashboard.driversBusyDroppingOff();
  const showBusySectionToggle = !!(busyPickingUp.length && busyDroppingOff.length);

  const driversAwaitingDeliveriesArr = dashboard.driversAwaitingDeliveries();
  const driversAwaitingDeliveries = descriptiveDriverList("Waiting for orders", driversAwaitingDeliveriesArr);
  const driversIdle = descriptiveDriverList("Idle", dashboard.driversIdle());
  const driversBusyPickingUp = descriptiveDriverList("Busy (picking up)", busyPickingUp, showBusySectionToggle);
  const driversBusyDroppingOff = descriptiveDriverList("Busy (dropping off)", busyDroppingOff, showBusySectionToggle);
  const driversBusyFirst = busySection ? driversBusyPickingUp : driversBusyDroppingOff;
  const driversBusySecond = !busySection ? driversBusyPickingUp : driversBusyDroppingOff;
  const driversRostered = descriptiveDriverList("Rostered but not active", dashboard.driversRostered());
  const driversRosteredToStartSoon = descriptiveDriverList("Rostered to start soon", dashboard.driversRosteredToStartSoon());

  const unallocatedCurrent = dashboard.unallocatedCurrent();
  const unallocatedFuture = dashboard.unallocatedFuture();


  useEffect(() => {
    setPreviousOrderNotificationAt(unallocatedCurrent.length);

    if (document.hasFocus()) return;

    // Update tab title
    //  on any change to the incoming order count
    if (unallocatedCurrent.length != previousOrderNotificationAt)
      setTabNotification(prevState => ( { ...prevState, order: unallocatedCurrent.length } ));

    // Play a sound
    if (unallocatedCurrent.length > previousOrderNotificationAt) {
      const soundEvent = new CustomEvent("soundEvent");
      window.dispatchEvent(soundEvent);
    }
  }, [unallocatedCurrent.length, previousOrderNotificationAt]);

  useEffect(() => {
    setPreviousDriverNotificationAt(driversAwaitingDeliveriesArr.length);

    if (document.hasFocus()) return;

    // Update tab title
    //  when the number of drivers awaiting deliveries goes from 0 -> 1+
    if (previousDriverNotificationAt == 0 && driversAwaitingDeliveriesArr.length > 0)
      setTabNotification(prevState => ( { ...prevState, driver: true } ));
  }, [driversAwaitingDeliveriesArr.length, previousDriverNotificationAt]);

  // Handle updating the tab title, with multiple independent notifications
  useEffect(() => {
    const driverNotification = tabNotification.driver ? "! " : "";
    const orderNotification = tabNotification.order > 0 ? `(${tabNotification.order}) ` : "";
    document.title = driverNotification + orderNotification + originalDocumentTitle.current;
  }, [tabNotification]);

  const [ columnCount, setColumnCount ] = useState(AdminPreferencesTracker.instance().preferences?.dashboard?.columns || 3)
  const updateFromPrefs = (prefs: AdminPreferences) => { setColumnCount(prefs.dashboard.columns) }

  useEffect(() => {
    AdminPreferencesTracker.instance().register(updateFromPrefs);
  }, []);

  return (
    <DashboardContext.Provider value={new DashboardConfig(apiClient)}>
      <CurrentTimeContext.Provider value={now}>
        <DragDropProvider
          sensors={[SmartMouseSensor]}
          onDragStart={handleDragStart}
          onDragEnd={(event) => void handleDragEnd(event)}
        >
          <div className={"dispatch-dashboard has-background-grey-lighter is-size-7 " + (connected ? '' : 'is-disconnected') + (hasCurfewHighlight ? " highlight-restricted" : "")} style={{ '--dashboard-column-count': columnCount } as React.CSSProperties} >
            <DragOverlay className="drag-overlay" style={{ zIndex: 99 }}>{draggedNodeClone}</DragOverlay>
            <HeatMap slots={dashboard.heatMap()} />

            <div className="columns px-3 mx-0">
              <div className="column is-one-quarter">
                <div className="is-flex mb-4 is-align-items-center">
                  <span className="is-size-5">Incoming orders</span>
                  <span className="tag is-rounded is-white is-light ml-2">{unallocatedCurrent.length}</span>
                </div>

                <Other.UnallocatedDeliveryList
                  unallocatedDeliveries={unallocatedCurrent}
                  dashboard={dashboard}
                />

                <div className="is-flex mb-4 is-align-items-center">
                  <span className="is-size-5">Future orders</span>
                  <span className="tag is-rounded is-white is-light ml-2">{unallocatedFuture.length}</span>
                </div>

                <div className="is-half-opacity">
                  <Other.UnallocatedDeliveryList
                    unallocatedDeliveries={unallocatedFuture}
                    dashboard={dashboard}
                  />
                </div>
              </div>

              <div className="column">
                <StatusBar dashboard={dashboard} currentAdminId={currentAdminId} />
                {deliveryGroupList}

                {driversAwaitingDeliveries}
                {driversIdle}
                {driversBusyFirst}
                {driversBusySecond}
                {driversRostered}
                {driversRosteredToStartSoon}
              </div>
            </div>
          </div>
        </DragDropProvider>
      </CurrentTimeContext.Provider>
    </DashboardContext.Provider>
  );
}

// This prevents dragging on elements & their children, via data-nodnd="true" attr
// https://github.com/clauderic/dnd-kit/issues/477
class SmartMouseSensor extends PointerSensor {
  static activators = [{
    eventName: "onPointerDown" as const,
    handler: ({ nativeEvent: event }: { nativeEvent: PointerEvent }) => {
      let cur: HTMLElement | null = event.target as HTMLElement;

      while (cur) {
        if (cur.dataset?.nodnd) {
          return false;
        }
        cur = cur.parentElement;
      }

      return true;
    }
  }];
}

const DroppableGroupNode = ({
  group,
  groupEl,
  containsDeliveries,
}: {
  group: DeliveryGroup;
  groupEl: ReactNode;
  containsDeliveries: boolean;
}): React.JSX.Element => {
  const { isDropTarget, ref } = useDroppable({
    id: `group:${group.id}`,
    data: {
      id: group.id,
      type: "group"
    }
  });

  const optionalClasses = classNames({
    "has-draggable-hover": isDropTarget,
    "group-placeholder": !containsDeliveries,
  });

  return (
    <div className={`column group ${optionalClasses}`} ref={ref}>
      {groupEl}
    </div>
  );
}

interface DeliveryGroupListProps {
  dashboard: Dashboard;
  deliveriesByGroup: Map<number, Delivery[]>;
  apiClient: ApiClientInterface;
}

const DeliveryGroupList = ({ dashboard, deliveriesByGroup, apiClient } : DeliveryGroupListProps) => {
  const handleRegroups = useCallback(async () => {
    await apiClient.compactGroups();
  }, [ apiClient ]);

  const handleOrderChronologically = useCallback(async () => {
    await apiClient.orderGroupsChronologically();
  }, [ apiClient ]);

  const allActiveGroupIds = Array.from(deliveriesByGroup.keys());
  const activeDeliveryGroups = dashboard.groups.filter(og => allActiveGroupIds.includes(og.id));

  const totalActiveGroups = activeDeliveryGroups.length;

  const groupBlocks = dashboard.groups.map((group) => {
    const deliveries = deliveriesByGroup.get(group.id) ?? [];
    const groupEl = deliveries.length > 0 ?
      <Other.DeliveryGroupComponent
        key={group.id}
        dashboard={dashboard}
        group={group}
        deliveries={deliveries}
      /> : <></>;

    return (
      <DroppableGroupNode
        group={group}
        groupEl={groupEl}
        containsDeliveries={deliveries.length > 0}
        key={group.id}
      />
    );
  });

  return (
    <>
      <div className="is-flex mb-4 is-align-items-center">
        <span className="is-size-5">Groups</span>
        <span className="tag is-rounded is-white is-light ml-2">{totalActiveGroups}</span>
        <button className="button is-small is-text ml-2 has-tooltip-bottom has-tooltip-arrow" data-tooltip="Tidy up" onClick={() => void handleRegroups()}>
          <span className="icon is-small">
            <i className="fas fa-th" aria-hidden="true"></i>
          </span>
        </button>
        <button className="button is-small is-text ml-2 has-tooltip-bottom has-tooltip-arrow" data-tooltip="Sort by pickup time" onClick={() => void handleOrderChronologically()}>
          <span className="icon is-small">
            <i className="fas fa-timer" aria-hidden="true"></i>
          </span>
        </button>
      </div>
      <div className="group-list columns is-multiline">
        {groupBlocks}
      </div>
    </>
  );
}

const StatusBar = ({
 dashboard,
 currentAdminId
} : {
  dashboard: Dashboard;
  currentAdminId: number;
}) => {
  const { apiClient } = useDashboardConfig();
  const currentTime = useContext(CurrentTimeContext);

  const thirtyMinutesOverdue = (delivery: Delivery) => {
    return pickupOverdueBy(delivery, 30) || deliveryOverdueBy(delivery, 30);
  }

  const pickupOverdueBy = (delivery: Delivery, minutes: number) => {
    return !delivery.pickedUpAt && currentTime.getTime() - delivery.pickupTimeIso8601.getTime() > minutes*60*1000;
  }

  const deliveryOverdueBy = (delivery: Delivery, minutes: number) => {
    return currentTime.getTime() - delivery.dropoffTimeIso8601.getTime() > minutes*60*1000;
  }

  const deliveriesUnacknowledgedByDriversCount = dashboard.deliveries.filter(delivery => delivery.driverReviewing()).length;
  const driversCanDeliverAgeRestrictedCount = dashboard.driversActive().filter(driver => driver.alcoholDeliveryAllowed).length;
  const deliveriesThirtyMinutesOverdueCount = dashboard.deliveries.filter(delivery => thirtyMinutesOverdue(delivery)).length;


  const takeDashButton = () => {
    if (currentAdminId == dashboard.dispatcherId) {
      return;
    }

    const handleUpdateDispatcher = async () => {
      await apiClient.updateDispatcher();
    };

    return <button className="button tag" onClick={() => void handleUpdateDispatcher()}>
      <span className="icon is-small mr-1">
        <i className="fa-solid fa-share" aria-hidden="true"></i>
      </span>
      Take dash
    </button>;
  }

  const activeDispatcher = () => {
    if (currentAdminId == dashboard.dispatcherId) {
      return;
    }

    if (dashboard.dispatcherId === null) {
      return <span className="tag no-dispatcher-warning">No dispatcher</span>;
    }

    return <span className="tag has-background-black has-text-white">Dispatcher: {dashboard.dispatcherName}</span>;
  }

  const deliveriesUnacknowledgedByDrivers = deliveriesUnacknowledgedByDriversCount > 0
    ? <span className="tag unacknowledged-deliveries-count" title="Number of orders that are unacknowledged by drivers">{deliveriesUnacknowledgedByDriversCount} order{deliveriesUnacknowledgedByDriversCount == 1 ? "" : "s"} unacknowledged</span>
    : <></>;

  const driversCanDeliverAgeRestricted = driversCanDeliverAgeRestrictedCount < 2
    ? <span className="tag" title="Number of drivers that can deliver age restricted goods">{driversCanDeliverAgeRestrictedCount} R18 driver{driversCanDeliverAgeRestrictedCount == 1 ? "" : "s"}</span>
    : <></>;

  const deliveriesThirtyMinutesOverdue = deliveriesThirtyMinutesOverdueCount > 0
    ? <span className="tag thirty-minutes-overdue-count" title="Number of orders that are thirty minutes overdue">{deliveriesThirtyMinutesOverdueCount} order{deliveriesThirtyMinutesOverdueCount == 1 ? "" : "s"} overdue</span>
    : <></>;


  return (
    <div className="status-bar is-flex is-flex-wrap-wrap is-gap-1.5 mb-3">
      {takeDashButton()}
      {activeDispatcher()}
      {deliveriesUnacknowledgedByDrivers}
      {driversCanDeliverAgeRestricted}
      {deliveriesThirtyMinutesOverdue}
    </div>
  );
}

export default DispatchDashboard;